import React, { useState } from "react";
import { Plus, Pencil, Trash2, Download, FileSignature } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, downloadPdf } from "@/lib/api";
import { PrimaryButton, GhostButton, EmptyState, Field, inputCls, formatCOP, StatusBadge } from "@/components/common";
import { DocTable, IconBtn, ClientSelect, ShareButtons } from "./shared";

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({
  client_id: "", client_name: "", client_nit: "", client_rep: "", client_rep_id: "", client_email: "", client_phone: "",
  object: "", scope_text: "", start_date: today(), duration_months: 6, duration_text: "", total_value: 0,
  payment_terms: "", signed_at: "", status: "borrador",
});
const STATUSES = ["borrador", "enviado", "firmado", "finalizado"];

export default function ContratosTab({ contracts, clients, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);
  const clientMap = Object.fromEntries(clients.map(c => [c.client_id, c]));

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (c) => { setEditing(c.contract_id); setForm({ ...empty(), ...c, scope_text: (c.scope_items || []).join("\n"), signed_at: c.signed_at || "" }); setOpen(true); };

  const pickClient = (client_id) => {
    const c = clients.find(x => x.client_id === client_id);
    setForm(f => ({
      ...f, client_id,
      client_name: c?.name || "", client_nit: c?.nit || "", client_rep: c?.legal_rep || c?.contact_name || "",
      client_rep_id: c?.legal_rep_id || "", client_email: c?.email || "", client_phone: c?.phone || "",
      total_value: f.total_value || c?.monthly_value || 0,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    const { scope_text, ...rest } = form;
    const payload = {
      ...rest, scope_items: scope_text.split("\n").map(s => s.trim()).filter(Boolean),
      duration_months: Number(form.duration_months) || 0, total_value: Number(form.total_value) || 0, signed_at: form.signed_at || null,
    };
    try {
      if (editing) await api.put(`/contracts/${editing}`, payload);
      else await api.post("/contracts", payload);
      toast.success(editing ? "Contrato actualizado" : "Contrato creado");
      setOpen(false); reload();
    } catch { toast.error("No se pudo guardar el contrato"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar el contrato ${c.number}?`)) return;
    await api.delete(`/contracts/${c.contract_id}`);
    toast.success("Contrato eliminado"); reload();
  };

  const pdf = async (c) => {
    try { await downloadPdf(`/contracts/${c.contract_id}/pdf`, `${c.number}.pdf`); }
    catch { toast.error("No se pudo generar el PDF"); }
  };

  return (
    <div data-testid="contratos-tab">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <p className="text-sm text-[#9CA3AF]">Contratos generados con tu plantilla de cláusulas. Edita el texto base en Configuración.</p>
        <PrimaryButton data-testid="add-contract-btn" onClick={openNew}><Plus className="w-4 h-4"/> Nuevo contrato</PrimaryButton>
      </div>
      {contracts.length === 0 ? (
        <EmptyState icon={FileSignature} title="Aún no tienes contratos" description="Crea un contrato desde cero o conviértelo desde una cotización aprobada."
          action={<PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Crear contrato</PrimaryButton>}/>
      ) : (
        <DocTable headers={["Número", "Cliente", "Objeto", "Inicio", "Plazo", { label: "Valor", right: true }, "Estado", { label: "Acciones", right: true }]}>
          {contracts.map(c => (
            <tr key={c.contract_id} data-testid={`contract-row-${c.contract_id}`} className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors">
              <td className="px-5 py-3 text-[#F5F5F7] font-mono text-xs">{c.number}</td>
              <td className="px-5 py-3 text-[#F5F5F7]">{c.client_name}</td>
              <td className="px-5 py-3 text-[#9CA3AF] max-w-[220px] truncate">{c.object}</td>
              <td className="px-5 py-3 text-[#9CA3AF]">{c.start_date}</td>
              <td className="px-5 py-3 text-[#9CA3AF]">{c.duration_text || `${c.duration_months} meses`}</td>
              <td className="px-5 py-3 text-right text-[#F5F5F7] font-medium">{formatCOP(c.total_value)}</td>
              <td className="px-5 py-3"><StatusBadge status={c.status}/>{c.acceptance && <span className="ml-1 text-[10px] text-emerald-400" title={`Firmado por ${c.acceptance.name}`}>✓ firma</span>}</td>
              <td className="px-5 py-3 text-right">
                <div className="inline-flex gap-1">
                  <IconBtn data-testid={`contract-pdf-${c.contract_id}`} onClick={() => pdf(c)} className="text-[#F97316] hover:bg-[#F97316]/10" title="Descargar PDF" aria-label="PDF"><Download className="w-4 h-4"/></IconBtn>
                  <ShareButtons path={`/contracts/${c.contract_id}`} clientEmail={clientMap[c.client_id]?.email ?? ""} testId={`contract-${c.contract_id}`} onSent={reload}/>
                  <IconBtn data-testid={`contract-edit-${c.contract_id}`} onClick={() => openEdit(c)} className="text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#262A33]" aria-label="Editar"><Pencil className="w-4 h-4"/></IconBtn>
                  <IconBtn onClick={() => remove(c)} className="text-[#6B7280] hover:text-red-400 hover:bg-red-500/10" aria-label="Eliminar"><Trash2 className="w-4 h-4"/></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </DocTable>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editing ? `Editar contrato ${form.number || ""}` : "Nuevo contrato"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Cliente *"><ClientSelect clients={clients} value={form.client_id} onChange={pickClient} testId="contract-client-select"/></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Razón social del contratante *"><input data-testid="contract-client-name" required className={inputCls} value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}/></Field>
              <Field label="NIT del contratante"><input className={inputCls} value={form.client_nit} onChange={e => setForm({ ...form, client_nit: e.target.value })}/></Field>
              <Field label="Representante legal"><input className={inputCls} value={form.client_rep} onChange={e => setForm({ ...form, client_rep: e.target.value })}/></Field>
              <Field label="Cédula del representante"><input className={inputCls} value={form.client_rep_id} onChange={e => setForm({ ...form, client_rep_id: e.target.value })}/></Field>
              <Field label="Email"><input className={inputCls} value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })}/></Field>
              <Field label="Teléfono"><input className={inputCls} value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })}/></Field>
            </div>
            <Field label="Objeto del contrato *" hint='Ej. "SERVICIO MANEJO DE REDES SOCIALES"'>
              <input data-testid="contract-object-input" required className={inputCls} value={form.object} onChange={e => setForm({ ...form, object: e.target.value })}/>
            </Field>
            <Field label="Alcance (una actividad por línea)">
              <textarea data-testid="contract-scope-input" rows={5} className={inputCls} value={form.scope_text} onChange={e => setForm({ ...form, scope_text: e.target.value })} placeholder={"Acompañamiento semanal…\nCampañas ADS Facebook e Instagram…\nGeneración de 12 videos al mes…"}/>
            </Field>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Fecha de inicio *"><input required type="date" className={inputCls} value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}/></Field>
              <Field label="Plazo (meses)"><input type="number" min="0" className={inputCls} value={form.duration_months} onChange={e => setForm({ ...form, duration_months: e.target.value })}/></Field>
              <Field label="Plazo en texto" hint="Opcional, ej. Seis (6) meses"><input className={inputCls} value={form.duration_text} onChange={e => setForm({ ...form, duration_text: e.target.value })}/></Field>
              <Field label="Valor total (COP) *"><input data-testid="contract-value-input" required type="number" min="0" className={inputCls} value={form.total_value} onChange={e => setForm({ ...form, total_value: e.target.value })}/></Field>
            </div>
            <Field label="Forma de pago *">
              <textarea data-testid="contract-payment-input" required rows={2} className={inputCls} value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} placeholder="Mes anticipado 50% y 50% a mitad de mes (pagos los 30 y 15 de cada mes)…"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha de firma" hint="Si está vacía se usa la fecha de inicio."><input type="date" className={inputCls} value={form.signed_at} onChange={e => setForm({ ...form, signed_at: e.target.value })}/></Field>
              <Field label="Estado">
                <select data-testid="contract-status-select" className={inputCls} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <DialogFooter>
              <GhostButton type="button" onClick={() => setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-contract-btn" type="submit">{editing ? "Guardar" : "Crear contrato"}</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
